/**
 * PATH: apps/qa/src/main.tsx
 * PURPOSE: QA Hub main entry point — initialise API client, recover stale
 *          IndexedDB / service-worker state, then render the React tree.
 *
 * WHY the IndexedDB cleanup?
 *   Workbox (via VitePWA) creates IndexedDB databases for cache-expiration
 *   and background-sync tracking. When we deploy a new service-worker the
 *   old databases can become schema-incompatible, producing:
 *     "NotFoundError: one of the specified object stores was not found"
 *   Clearing them on startup prevents the debugger-pause the user sees.
 *
 * DEPENDENCIES:
 * - @quotech/auth for authentication
 * - @quotech/api for API client and React Query
 * - @quotech/ui for layout components
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ApiProvider } from '@quotech/api'
import { installChunkErrorHandler, installClientErrorReporter } from '@quotech/ui'
import {
  getAuthHeadersForDiagnostics,
  getOrganisationIdForDiagnostics,
} from '@quotech/auth'
import App from './App'
import { initializeApi } from './lib/setupApi'
import './index.css'

// Handle stale chunk 404s after deployment (auto-reload)
installChunkErrorHandler()
installClientErrorReporter({
  buildSha: import.meta.env.VITE_BUILD_SHA,
  getAuthHeaders: getAuthHeadersForDiagnostics,
  getOrganisationId: getOrganisationIdForDiagnostics,
})

// ── Recover from stale / conflicting IndexedDB databases ────────────────────
// Previously offlineStore.ts and offline-db.ts both used "quotech-qa-offline"
// with different schemas → NotFoundError on object store access.
// offlineStore.ts was renamed to "quotech-qa-uploads". On first load after
// deploy we delete the old conflicting DB so both modules start fresh.
//
// Also cleans up stale Workbox DBs that become schema-incompatible after
// service-worker updates.
;(async () => {
  if (!('indexedDB' in window) || !indexedDB.databases) return
  try {
    const databases = await indexedDB.databases()
    // DB names that should be probed and deleted if corrupt
    const MANAGED_DBS = new Set([
      'quotech-qa-offline',
      'quotech-qa-uploads',
      'qa-offline-db',
    ])
    for (const db of databases) {
      if (!db.name) continue
      const shouldCheck = MANAGED_DBS.has(db.name) || /^workbox-/.test(db.name)
      if (!shouldCheck) continue

      // Probe the DB to see if it's healthy; if not, nuke it
      try {
        const req = indexedDB.open(db.name)
        await new Promise<void>((resolve, reject) => {
          req.onsuccess = () => { req.result.close(); resolve() }
          req.onerror = () => reject(req.error)
        })
      } catch {
        console.warn(`[QA Hub] Deleting corrupt IndexedDB: ${db.name}`)
        indexedDB.deleteDatabase(db.name)
      }
    }
  } catch {
    // Non-critical — swallow and continue boot
  }
})()

// Initialize API client with auth store
initializeApi()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ApiProvider devtools>
      {/* unstable_useTransitions={false} — see apps/newmark/src/main.tsx. (qf!3010, May 2026) */}
      <BrowserRouter unstable_useTransitions={false}>
        <App />
      </BrowserRouter>
    </ApiProvider>
  </React.StrictMode>,
)
